// Workforce › Agents (`/c/{slug}/agents/ag`; WORKFORCE, Andrei 2026-10-26): the agents (AG) —
// task executors with no seat, KPIs or budget — one 36 px row each: the AG badge and name (a
// link to the agent's own page, `/agents/ag/{id}`; WF-FIX), its team, whom it reports to (a DE
// or a person, badged), where it runs ("Hermes · <container> · ClawPanel #<n>"), its status and
// its latest activity with a readable time. Every member sees every agent; the people above one
// (and managers: `can_edit`) act on it from the row's hover icons — Pause / Resume, Take over
// (report to me), Reassign to a DE or a person, Edit (name, team), Retire (`ag-actions.tsx`; each
// lifecycle icon says it changes the record, not the container); a refused change toasts its
// reason. Agents are registered by ops (`infra/ops/register-agents.ts`) or, since WF-POLISH,
// added here ("Add agent" in the header, `add-ag-dialog.tsx`). One call on load (the list; the sidebar and the seat tree share it), plus, for someone
// who may change an agent, every page of the DEs, people and teams.
import {
  Badge,
  Button,
  EmptyState,
  ListRow,
  LocationHeader,
  SearchField,
  Toolbar,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { WorkflowIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { WorkforceAgentRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { workforceAgentsQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { AddAgentButton } from "./add-ag-dialog";
import {
  AG_STATUS,
  AgActions,
  type AgChoices,
  activityLabel,
  runtimeLabel,
  useAgChoices,
} from "./ag-actions";
import { LIST_BODY } from "./agents-ux";
import { HelpersInfoTip } from "./helpers";
import { RecallKeysChip } from "./recall-keys-chip";

export function AgRosterScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  // "Show retired" lists retired agents too, so one can be reactivated.
  const [showRetired, setShowRetired] = useState(false);
  const agents = useQuery(workforceAgentsQuery(api, company.id, showRetired));
  const [search, setSearch] = useState("");
  const rows = agents.data ?? [];
  // Reassign and Edit choices, loaded (every page) only when the viewer may change an agent.
  const choices = useAgChoices(
    api,
    company.id,
    rows.some((a) => a.can_edit),
  );
  const q = search.trim().toLowerCase();
  const shown = q
    ? rows.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.team?.name ?? "").toLowerCase().includes(q) ||
          a.reports_to.name.toLowerCase().includes(q),
      )
    : rows;
  const counts = { active: 0, paused: 0, retired: 0, not_deployed: 0 };
  for (const a of rows) counts[a.status] += 1;
  // HELPERS (design §1): grouped by lead — "Marketing Director (14)", "Salesperson (1)".
  const groups = groupByLead(shown);
  return (
    <div className={LIST_BODY} data-slot="ag-roster">
      <LocationHeader
        icon={WorkflowIcon}
        title={
          <span className="inline-flex items-center gap-1">
            Helpers
            <HelpersInfoTip />
          </span>
        }
        {...(agents.isSuccess ? { count: rows.length } : {})}
        actions={<AddAgentButton api={api} company={company} variant="labelled" />}
      />
      <Toolbar bleed aria-label="Helpers tools">
        <SearchField
          value={search}
          placeholder="Search helpers…"
          aria-label="Search helpers"
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums" aria-live="polite">
          {(["active", "paused", "not_deployed", "retired"] as const)
            .filter((k) => counts[k])
            .map((k) => `${counts[k]} ${AG_STATUS[k].label.toLowerCase()}`)
            .join(" · ") || (agents.isSuccess ? "No helpers" : "…")}
        </span>
        <Button
          type="button"
          size="sm"
          variant={showRetired ? "secondary" : "ghost"}
          aria-pressed={showRetired}
          onClick={() => setShowRetired((v) => !v)}
        >
          Show retired
        </Button>
      </Toolbar>
      {agents.isPending ? (
        <div className="px-6 py-4 max-md:px-4">
          <Loading label="Loading agents" rows={3} />
        </div>
      ) : agents.isError ? (
        <div className="px-6 py-4 max-md:px-4">
          <LoadError what="Agents" error={agents.error} />
        </div>
      ) : !rows.length ? (
        <EmptyState
          variant="compact"
          icon={WorkflowIcon}
          title="No helpers yet"
          description="Task executors with no seat, KPIs or budget. Add one with Add agent."
          className="m-6"
        />
      ) : (
        <div data-slot="ag-rows">
          {groups.map((g) => (
            <section key={g.id} data-ag-group={g.id} aria-label={`${g.name}'s helpers`}>
              <h3
                className="flex items-center gap-1.5 border-b border-subtle bg-muted/40 px-6 py-1 text-xs font-medium text-muted-foreground max-md:px-4"
                data-slot="ag-group-head"
              >
                <WorkerBadge kind={g.kind === "digital_employee" ? "de" : "person"} size="sm" />
                <span className="truncate text-foreground">{workerName(g.name)}</span>
                <span className="tabular-nums">({g.rows.length})</span>
              </h3>
              {g.rows.map((a) => (
                <AgRow key={a.id} api={api} company={company} agent={a} choices={choices} />
              ))}
            </section>
          ))}
          {q && !shown.length ? (
            <p className="px-6 py-3 text-sm text-muted-foreground max-md:px-4">
              No helper matches "{search.trim()}".
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** The rows by whom they report to, leads by name, each group in the list's order. */
export function groupByLead(rows: readonly WorkforceAgentRow[]) {
  const out = new Map<
    string,
    {
      id: string;
      name: string;
      kind: WorkforceAgentRow["reports_to"]["kind"];
      rows: WorkforceAgentRow[];
    }
  >();
  for (const a of rows) {
    const g = out.get(a.reports_to.membership_id) ?? {
      id: a.reports_to.membership_id,
      name: a.reports_to.name,
      kind: a.reports_to.kind,
      rows: [],
    };
    g.rows.push(a);
    out.set(g.id, g);
  }
  return [...out.values()].sort((x, y) => workerName(x.name).localeCompare(workerName(y.name)));
}

function AgRow({
  api,
  company,
  agent: a,
  choices,
}: {
  api: Api;
  company: CompanyAccess;
  agent: WorkforceAgentRow;
  choices: AgChoices;
}) {
  const name = workerName(a.name);
  const runtime = runtimeLabel(a.runtime);
  const status = AG_STATUS[a.status];
  const activity = activityLabel(a);
  return (
    <ListRow
      data-row
      data-ag={a.id}
      data-ag-status={a.status}
      lead={<WorkerBadge kind="ag" size="sm" />}
      title={name}
      // WF-FIX: the row opens the agent's own page.
      render={
        <Link
          to="/c/$slug/agents/ag/$id"
          params={{ slug: company.slug, id: a.id }}
          search={keepFocus}
        />
      }
      chips={
        <>
          {a.team ? (
            <Badge variant="outline" className="max-sm:hidden">
              {a.team.name}
            </Badge>
          ) : null}
          <Badge variant={status.variant}>{status.label}</Badge>
          <RecallKeysChip agent={a} />
        </>
      }
      meta={
        <span className="inline-flex min-w-0 items-center gap-1.5" data-slot="ag-reports-to">
          <span className="text-muted-foreground">Reports to</span>
          <WorkerBadge
            kind={a.reports_to.kind === "digital_employee" ? "de" : "person"}
            size="sm"
          />
          <span className="truncate">{workerName(a.reports_to.name)}</span>
          {runtime ? (
            <span className="truncate text-muted-foreground max-md:hidden" data-slot="ag-runtime">
              · {runtime}
            </span>
          ) : null}
        </span>
      }
      trailing={
        <span
          className="inline-flex min-w-0 items-center gap-1.5"
          data-slot="ag-activity"
          title={activity ? [activity.text, activity.full].filter(Boolean).join(" · ") : undefined}
        >
          <span className="truncate">{activity?.text ?? "No activity reported"}</span>
          {activity?.when ? (
            <time
              dateTime={a.last_activity_at ?? undefined}
              className="shrink-0 text-muted-foreground tabular-nums"
              data-slot="ag-activity-at"
            >
              {activity.when}
            </time>
          ) : null}
        </span>
      }
      actions={<AgActions api={api} company={company} agent={a} choices={choices} variant="row" />}
    />
  );
}
